import { useState } from "react";
import type { StockLocationDto } from "@grizzly/shared";
import { api, ApiClientError } from "../../lib/apiClient.js";
import { Button } from "../ui/button.js";
import { SelectInput, TextInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";
import { useI18n } from "../../i18n/index.js";

export function AdjustStockForm({
  materialId,
  locations,
  onAdjusted,
}: {
  materialId: string;
  locations: StockLocationDto[];
  onAdjusted(): void;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [locationId, setLocationId] = useState(locations[0]?.id ?? "");
  const [direction, setDirection] = useState<"in" | "out">("in");
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.adjustStock({ materialId, locationId, direction, quantity, reason });
      push(t("inventory.adjustmentRecorded"));
      setQuantity("");
      setReason("");
      onAdjusted();
    } catch (err) {
      if (err instanceof ApiClientError) push(err.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 rounded border border-line p-3">
      <div className="grid gap-2 sm:grid-cols-2">
        <SelectInput
          label={t("inventory.location")}
          options={locations.map((l) => ({ value: l.id, label: l.name }))}
          value={locationId}
          onChange={(e) => setLocationId(e.target.value)}
        />
        <SelectInput
          label={t("inventory.direction")}
          options={[
            { value: "in", label: t("inventory.stockIn") },
            { value: "out", label: t("inventory.stockOut") },
          ]}
          value={direction}
          onChange={(e) => setDirection(e.target.value as "in" | "out")}
        />
      </div>
      <TextInput
        label={t("common.fields.quantity")}
        value={quantity}
        onChange={(e) => setQuantity(e.target.value)}
      />
      <TextInput
        label={t("common.fields.reason")}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        hint={t("inventory.reasonHint")}
      />
      <Button
        variant="primary"
        disabled={busy || !quantity || reason.trim().length < 3}
        onClick={submit}
      >
        {busy ? t("common.saving") : t("inventory.recordAdjustment")}
      </Button>
    </div>
  );
}
